import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as d3 from 'd3';
import styled from 'styled-components';
import _ from 'lodash';
import warningIcon from '../static/images/orange_warning_symbol.png';
import dataIcon from '../static/sprite/sources/small/data.svg';
import voiceIcon from '../static/sprite/sources/small/chat.svg';
import infinityIcon from '../static/images/icon_anim_data_ul100.svg';
import legendIconConsumptionInQuota from '../static/images/dial_legend_consumption_in_package.svg';
import legendIconConsumptionOverQuota from '../static/images/dial_legend_consumption_exceeded.svg';
import legendIconConsumptionEstimate from '../static/images/dial_legend_comsumption_estimate.svg';
import { Icon } from './Icon';

interface Props {
	consumption: number;
	packageSize: number;
	warningValue: number;
	estimate: number;
	maxValue: number;
	type: 'data' | 'voice';
	isOngoingBillingPeriod: boolean;
}

const containerWidth = 340;
const containerHeight = 250;

const dialArcInnerRadius = 142;
const dialBarWidth = 8;
const dialStartDegrees = -120;
const dialLengthDegrees = 240;
const dialEndDegrees = dialLengthDegrees + dialStartDegrees;
const lineGapDegrees = 3;
const lineWidth = 2;
const lineDistance = 7;
const lineSashLength = 10;
const lineRadius = dialArcInnerRadius - lineDistance;

const bgArcBlockGap = 1.5;
const bgArcBlockWidth = 1.5;

const colorBackground = '#e0e0e0';
const colorBackgroundLine = '#666666';
const colorInsideQuota = '#94C92D';
const colorOutsideQuota = '#F48945';
const colorShouldUpgrade = '#FF0000';

const inRadian = (angle: number) => ((angle * Math.PI) / 180);
const getDialEndAngleDegrees = () => dialStartDegrees + dialLengthDegrees;
const getCircleX = (angle: number, radius: number) => radius * Math.sin(inRadian(angle));
const getCircleY = (angle: number, radius: number) => 0 - (radius * Math.cos(inRadian(angle)));
const getLocaleString = (myNumber: any) => _.isNumber(Number(myNumber.toLocaleString)) ? myNumber.toLocaleString() : 0;
const isUnlimited = (packageSize: number) => _.includes([Infinity, 'unlimited', 'Rajaton data'], packageSize);

const getArcLengthDegrees = (value: any, outOf: number) => {
	if (!isUnlimited(value) && _.isNumber(value) && _.isNumber(outOf)) {
		return (value / outOf) * dialLengthDegrees;
	}
	return 0;
};

const dialContainerStyle: any = {
	marginBottom: '2em',
	textAlign: 'center',
};

const Legend = styled.div`
  ul {
    list-style-type: none;
    margin: 0;
    padding: 0;
    text-align: left;
    margin-left: 2em;
  }

  li {
    background: none;
    padding-bottom: 1px;
    list-style-type: none;
    overflow: auto;
  }

  li .legend-icon {
    display: inline-block;
    width: 50px;
    background-size: 20px;
    background-position-x: 65%;
    height: 20px;
  }

  li .legend-description {
    grid-column-start: 2;
  }

  .dial-alert {
    margin-top: 1em;
  }
  .dial-alert .col1 {
    float: left;
    margin-left: 5px;
    margin-right: 15px;
  }

  .dial-alert .col2 {
    float: right;
    width: calc(100% - 50px);
  }

  .dial-alert .svg-icon {
  }

  .icon-warning {
    background: url(${warningIcon}) 0 0 no-repeat;
  }

  .icon-consumption-estimate {
    background: url(${legendIconConsumptionEstimate}) 0 0 no-repeat;
  }
  .icon-consumption-over-quota {
    background: url(${legendIconConsumptionOverQuota}) 0 0 no-repeat;
  }
  .icon-consumption-in-quota {
    background: url(${legendIconConsumptionInQuota}) 0 0 no-repeat;
  }
`;

export const Dial: React.FC<Props> = (props: Props) => {
	let dialUnit = '';
	const textY = 150;
	const svgId = `dial-svg-${props.type}`;
	const elementId = `dial-container-${props.type}`;
	const dialBackgroundId = `dial-background-${props.type}`;
	const dialLinesId = `dial-lines-${props.type}`;
	const dialConsumptionId = `dial-consumption-${props.type}`;
	let icon;
	let iconX;
	let iconY;
	let iconWidth = '34px';
	let dialSubtext1;
	let dialSubtext2;
	if (props.type === 'data') {
		if (isUnlimited(props.packageSize)) {
			icon = infinityIcon;
			iconX = 140;
			iconY = 74;
			iconWidth = '60px';
		} else {
			icon = dataIcon;
			iconX = 153;
			iconY = 65;
		}
		dialUnit = 'Gt';
		if (props.isOngoingBillingPeriod) {
			dialSubtext1 = 'dataa käytetty';
		} else {
			dialSubtext1 = 'datan kokonaiskulutus';
			dialSubtext2 = 'kaudella';
		}
	} else if (props.type === 'voice') {
		icon = voiceIcon;
		iconX = 153;
		iconY = 68;
		dialSubtext1 = 'puheminuuttia tai';
		dialSubtext2 = 'viestiä kulutettu';
	}

	const d3Container = useRef(null);

	const getConsumption = () => {
		const decimals = props.type === 'data' ? 2 : 0;
		return cleanNumber(_.isNaN(props.consumption) ? 0 : props.consumption, decimals);
	}

	const getConsumptionOverPackage = (packageSize: any, consumption: any) => {
		const decimals = props.type === 'data' ? 2 : 0;
		if (_.isNumber(packageSize)) {
			if (Number(consumption) < Number(packageSize)) {
				return 0;
			}
			return cleanNumber(Number(consumption) - Number(packageSize), decimals);
		}
		return 0;
	}

	const drawFilledArc = (startAngle: any, endAngle: any, innerRadius: any, width: any, color: any, containerId = svgId) => {
		const area = d3.select(d3Container.current).select(`#${containerId}`);
		const filledArc: any = d3.arc()  // why casting to "any" is needed?
			.innerRadius(innerRadius)
			.outerRadius(innerRadius + width)
			.startAngle(inRadian(startAngle))
			.endAngle(inRadian(endAngle));
		const domObj = area.append('path')
			.attr('fill', color)
			.attr('shapeRendering', 'geometricPrecision')
			.attr('transform', 'translate(170, 150)')
			.attr('d', filledArc);
		return domObj;
	}

	// Draw line marking size of package and point where it would be cheaper to switch to next package
	const drawQuotaStopsLine = () => {
		const okLength = getArcLengthDegrees(props.packageSize, props.maxValue);
		const warningLength = hasWarning() ? getArcLengthDegrees((props.warningValue - props.packageSize), props.maxValue) - lineGapDegrees : 0;
		const warningStart = dialStartDegrees + okLength + lineGapDegrees;
		const warningEnd = warningStart + warningLength;
		if ((isUnlimited(props.packageSize) && props.type === 'data') || !props.isOngoingBillingPeriod) {
			drawFilledArc(dialStartDegrees, dialEndDegrees, lineRadius, lineWidth, '#CCCCCC', dialLinesId);
		} else {
			// line segments
			drawFilledArc(dialStartDegrees, (dialStartDegrees + okLength), lineRadius, lineWidth, colorBackgroundLine, dialLinesId);
			drawFilledArc(warningStart, warningEnd, lineRadius, lineWidth, colorBackgroundLine, dialLinesId);
			drawFilledArc((warningEnd + lineGapDegrees), getDialEndAngleDegrees(), lineRadius, lineWidth, colorBackgroundLine, dialLinesId);
			// Sash at the end of consumption line
			drawFilledArc(((dialStartDegrees + okLength) - 1), (dialStartDegrees + okLength), (lineRadius - lineSashLength), lineSashLength, colorBackgroundLine, dialLinesId);
			if (hasWarning()) {
				// Sash at the end of warning line
				drawFilledArc(warningEnd, (warningEnd + 1), (lineRadius - lineSashLength) + 2, lineSashLength, colorBackgroundLine, dialLinesId);
			}
			// Change radii of x/y to et labels / warnings on an elliptical line that makes visually more sense for the positioning
			// First value is "center" of circle for coordinates (which from 0,0 go to negatives on top/left)
			const labelX = 167 + getCircleX(warningStart, (lineRadius - 8));
			const labelY = 170 + getCircleY(warningStart, (lineRadius - 8));
			const warningX = 170 + getCircleX(warningEnd, (lineRadius - 33));
			const warningY = 150 + getCircleY(warningEnd, (lineRadius - 10));
			// Only show warning if not unlimited package
			if (hasWarning()) {
				d3.select(`#${dialLinesId}`)
					.append('image')
					.attr('href', warningIcon)
					.attr('x', warningX)
					.attr('y', warningY);
			}
			d3.select(`#${dialLinesId}`)
				.append('text')
				.attr('x', labelX)
				.attr('y', labelY)
				.attr('font-family', 'Platform')
				.attr('font-size', '16px')
				.attr('text-anchor', 'middle')
				.attr('fill', '#444444')
				.text(`${props.packageSize} ${dialUnit}`);
		}
	}

	const drawEstimate = () => {
		const endAngle = Math.min(dialStartDegrees + getArcLengthDegrees(props.estimate, props.maxValue), getDialEndAngleDegrees());
		let currentAngle = dialStartDegrees;
		while (currentAngle < endAngle) {
			drawFilledArc(currentAngle, (currentAngle + bgArcBlockWidth), dialArcInnerRadius, dialBarWidth, colorBackground, dialBackgroundId);
			currentAngle = currentAngle + bgArcBlockGap + bgArcBlockWidth;
		}
	}

	const drawConsumption = () => {
		const warningValue = props.warningValue < 100000 ? props.warningValue : 0;
		const consumptionInPackage = props.consumption > props.packageSize ? props.packageSize : props.consumption;
		const consumptionEndDegrees = dialStartDegrees + getArcLengthDegrees(consumptionInPackage, props.maxValue);
		const orangeStart = dialStartDegrees + getArcLengthDegrees(props.packageSize, props.maxValue);
		const redStart = dialStartDegrees + getArcLengthDegrees(warningValue, props.maxValue);
		const consumptionShouldUpgrage = props.consumption - warningValue;
		// green
		drawFilledArc(dialStartDegrees, consumptionEndDegrees, dialArcInnerRadius, dialBarWidth, colorInsideQuota, dialConsumptionId);
		if (props.consumption > props.packageSize) { // draw orange arc
			const consumptionOverPackage = Math.min(props.consumption, warningValue) - props.packageSize;
			drawFilledArc((consumptionEndDegrees + lineGapDegrees), orangeStart + getArcLengthDegrees(consumptionOverPackage, props.maxValue), dialArcInnerRadius, dialBarWidth, colorOutsideQuota, dialConsumptionId);
			if (props.consumption > props.warningValue) { // draw red arc
				const consumptionEnd = redStart + getArcLengthDegrees(consumptionShouldUpgrage, props.maxValue);
				const redEnd = Math.min(dialEndDegrees, consumptionEnd);
				drawFilledArc((redStart + lineGapDegrees), redEnd, dialArcInnerRadius, dialBarWidth, colorShouldUpgrade, dialConsumptionId);
			}
		}
	}

	const cleanNumber = (value: any, decimals = 0) => {
		const val = _.isNumber(value) ? value : 0;
		return String(parseFloat(val as any).toFixed(decimals));
	}

	const hasWarning = () => {
		if (props.type === 'data') {
			return _.isNumber(props.warningValue) && isUnlimited(props.packageSize) === false;
		}
		return _.isInteger(props.warningValue) && isUnlimited(props.packageSize) === false;
	}

	const isCheaperToUpgradePackage = (usage: any, warningValue: any) => {
		if (_.isNumber(warningValue) && _.isNumber(usage)) {
			return (usage > warningValue);
		}
		return false;
	}

	const clearDial = () => {
		d3.select(`${elementId} > *`).remove();
	}

	useEffect(() => {
		clearDial();
		drawQuotaStopsLine();
		if (props.isOngoingBillingPeriod) {
			drawEstimate();
			drawConsumption();
		}
	}, [d3Container, props.consumption, props.packageSize, props.warningValue, props.estimate, props.maxValue, props.type, props.isOngoingBillingPeriod]); // eslint-disable-line

	return (
		<div style={dialContainerStyle} id={elementId}>
			<svg width={containerWidth} height={containerHeight} id={svgId} ref={d3Container}>
				<g y={0} x={containerWidth / 2} transform={`translate(${containerWidth / 2}, ${textY})`}>
					<text x="0" y="15" id="dial-consumption-value" fontFamily="Platform" fontSize="52px" textAnchor="middle" fill="rgb(76, 76, 76)">{getConsumption()} {dialUnit}</text>
					<line x1="-85" y1="29" x2="85" y2="29" stroke="rgb(230, 231, 231)" strokeWidth="1"></line>
					<text x="0" y="55" id="dial-consumption-subtext" fontFamily="Platform" fontSize="20px" textAnchor="middle" fill="rgb(76, 76, 76)">
						<tspan>{dialSubtext1}</tspan>
						<tspan dy={20} x="0">{dialSubtext2}</tspan>
					</text>
				</g>

				<image href={icon} x={iconX} y={iconY} width={iconWidth}></image>
				<g id={dialBackgroundId}></g>
				<g id={dialLinesId}></g>
				<g id={dialConsumptionId}></g>
			</svg>
			<Legend>
				{props.isOngoingBillingPeriod && (
					<ul>
						<li>
							<span className="col1 legend-icon icon-consumption-in-quota">&nbsp;</span>
							<span className="col2">{getLocaleString(props.packageSize)} {props.type === 'data' ? dialUnit : 'min/sms'} sisältyy kuukausihintaan</span>
						</li>
						{((isUnlimited(props.packageSize) && props.type === 'data') === false) && (
							<li>
								<span className="col1 legend-icon icon-consumption-over-quota">&nbsp;</span>
								<span className="col2">{getLocaleString(getConsumptionOverPackage(props.packageSize, getConsumption()))} {dialUnit} kuukausihintaan sisältymätön käyttö</span>
							</li>
						)}
						{(hasWarning() && (isUnlimited(props.packageSize) && props.type === 'data') === false) && (
							<li>
								<span className="col1 legend-icon icon-warning">&nbsp;</span>
								<span className="col2">{getLocaleString(props.warningValue)} {dialUnit} jälkeen on halvempaa ottaa isompi paketti</span>
							</li>
						)}
						<li>
							<span className="col1 legend-icon icon-consumption-estimate">&nbsp;</span>
							<span className="col2">Ennuste {getLocaleString(props.estimate)} {props.type === 'data' ? dialUnit : 'puheminuuttia tai viestiä'}</span>
						</li>
						{isCheaperToUpgradePackage(props.consumption, props.warningValue) && (
							<li className="dial-alert">
								<Icon className="col1" name={'small--bell'} />
								{props.type === 'data' && (
									<div className="col2">Simmit käyttävät dataa {getConsumptionOverPackage(props.packageSize, getConsumption())} Gt enemmän kuin kuukausihintaan sisältyy. Suosittelemme, että <Link to={'/account/order'}>tilaat isomman datapaketin.</Link></div>
								)}
								{props.type === 'voice' && (
									<div className="col2">Simmit käyttävät {getConsumptionOverPackage(props.packageSize, getConsumption())} minuuttia tai viestiä enemmän kuin kuukausihintaan sisältyy. Suosittelemme, että <Link to={'/account/order'}>tilaat isomman paketin.</Link></div>
								)}
							</li>
						)}
					</ul>
				)}
			</Legend>
		</div>
	);
};